import { z } from "zod";
import { entryCollectionSchema, kitEntryKindSchema, kitTitleFormatSchema } from "./kit.js";
export const apiErrorCodeSchema = z.enum([
    "AUTH_REQUIRED",
    "FORBIDDEN",
    "CSRF_INVALID",
    "VALIDATION_FAILED",
    "NOT_FOUND",
    "REVISION_CONFLICT",
    "IDEMPOTENCY_CONFLICT",
    "IDEMPOTENCY_UNKNOWN",
    "MODEL_UNAVAILABLE",
    "MODEL_FAILED",
    "RATE_LIMITED",
    "INTERNAL_ERROR",
]);
export const apiErrorSchema = z.object({
    error: z.object({
        code: apiErrorCodeSchema,
        message: z.string(),
        details: z.record(z.string(), z.unknown()).optional(),
    }),
});
export const loginRequestSchema = z.object({
    password: z.string().min(1).max(512),
});
export const sessionSchema = z.object({
    authenticated: z.literal(true),
    csrfToken: z.string().min(32),
    expiresAt: z.string().datetime(),
});
export const sortSchema = z.enum([
    "random",
    "name-asc",
    "name-desc",
    "stars-desc",
    "stars-asc",
    "question-desc",
    "question-asc",
    "recent",
]);
export const termListQuerySchema = z.object({
    search: z.string().trim().max(120).default(""),
    collection: z.union([entryCollectionSchema, z.literal("all")]).default("technical"),
    category: z.string().trim().max(80).default("all"),
    state: z.enum(["all", "stars-high", "conversation", "marker"]).default("all"),
    sort: sortSchema.default("random"),
    cursor: z.string().max(512).optional(),
    limit: z.coerce.number().int().min(1).max(50).default(20),
    randomSeed: z.coerce.number().int().min(0).max(2_147_483_647).default(1),
});
export const termSummarySchema = z.object({
    id: z.string().uuid(),
    slug: z.string(),
    name: z.string(),
    titleFormat: kitTitleFormatSchema,
    entryKind: kitEntryKindSchema,
    collection: entryCollectionSchema,
    summary: z.string(),
    category: z.string(),
    needUnderstanding: z.number().int().min(1).max(5),
    conversationCount: z.number().int().nonnegative(),
    markerCount: z.number().int().nonnegative(),
    lastQuestionAt: z.string().datetime().nullable(),
    lastViewedAt: z.string().datetime().nullable(),
    updatedAt: z.string().datetime(),
});
export const termListResponseSchema = z.object({
    items: z.array(termSummarySchema),
    collections: z.array(z.object({
        value: entryCollectionSchema,
        count: z.number().int().nonnegative(),
    })),
    categories: z.array(z.object({
        value: z.string(),
        count: z.number().int().nonnegative(),
    })),
    nextCursor: z.string().nullable(),
    total: z.number().int().nonnegative(),
    randomSeed: z.number().int().nonnegative(),
});
export const blockKindSchema = z.enum(["markdown", "code", "math"]);
export const clarificationSchema = z.object({
    id: z.string().uuid(),
    question: z.string(),
    answer: z.string(),
    sourceUserMessageId: z.string().uuid(),
    answerMessageId: z.string().uuid(),
    targetBlockId: z.string(),
    conversationId: z.string().uuid(),
    appliedRevisionId: z.string().uuid(),
    appliedRevisionVersion: z.number().int().positive(),
});
export const termBlockSchema = z.object({
    id: z.string(),
    kind: blockKindSchema,
    title: z.string(),
    body: z.string(),
    language: z.string().nullable(),
    order: z.number().int().nonnegative(),
    clarifications: z.array(clarificationSchema),
});
export const revisionSummarySchema = z.object({
    id: z.string().uuid(),
    version: z.number().int().positive(),
    source: z.enum(["kit", "website-ai", "manual-import"]),
    changeSummary: z.string(),
    createdAt: z.string().datetime(),
    conversationId: z.string().uuid().nullable(),
    affectedBlockIds: z.array(z.string()),
    questionPoints: z.array(clarificationSchema),
});
export const markerSchema = z.object({
    id: z.string().uuid(),
    blockId: z.string(),
    baselineRevisionId: z.string().uuid(),
    blockSnapshot: z.string(),
    note: z.string(),
    status: z.enum(["open", "resolved"]),
    createdAt: z.string().datetime(),
});
export const termDetailSchema = z.object({
    id: z.string().uuid(),
    slug: z.string(),
    name: z.string(),
    titleFormat: kitTitleFormatSchema,
    entryKind: kitEntryKindSchema,
    collection: entryCollectionSchema,
    aliases: z.array(z.string()),
    tags: z.array(z.string()),
    summary: z.string(),
    category: z.string(),
    context: z.string(),
    currentRevision: revisionSummarySchema,
    blocks: z.array(termBlockSchema),
    needUnderstanding: z.number().int().min(1).max(5),
    revisions: z.array(revisionSummarySchema),
    markers: z.array(markerSchema),
    updatedAt: z.string().datetime(),
});
export const learningStateRequestSchema = z.object({
    needUnderstanding: z.number().int().min(1).max(5),
});
export const learningStateResponseSchema = learningStateRequestSchema.extend({
    updatedAt: z.string().datetime(),
});
export const createMarkerRequestSchema = z.object({
    blockId: z.string().min(1).max(120),
    baselineRevisionId: z.string().uuid(),
    note: z.string().trim().min(1).max(4_000),
});
export const createConversationRequestSchema = z.object({
    entryBlockId: z.string().min(1).max(120),
    baselineRevisionId: z.string().uuid(),
});
export const messageSchema = z.object({
    id: z.string().uuid(),
    role: z.enum(["user", "assistant"]),
    content: z.string(),
    createdAt: z.string().datetime(),
});
export const conversationSchema = z.object({
    id: z.string().uuid(),
    termId: z.string().uuid(),
    entryBlockId: z.string(),
    baselineRevisionId: z.string().uuid(),
    status: z.enum(["open", "proposal-ready", "closed"]),
    messages: z.array(messageSchema),
    createdAt: z.string().datetime(),
});
export const modelRuntimeSchema = z.object({
    modelId: z.string().min(1),
    label: z.string().min(1),
    providerId: z.string().min(1),
    configured: z.boolean(),
});
export const createTurnRequestSchema = z.object({
    content: z.string().trim().min(1).max(8_000),
});
export const turnSchema = z.object({
    id: z.string().uuid(),
    status: z.enum(["pending", "running", "succeeded", "failed", "unknown", "cancelled"]),
    modelId: z.string(),
    userMessage: messageSchema,
    assistantMessage: messageSchema.nullable(),
    errorCode: z.string().nullable(),
    createdAt: z.string().datetime(),
    completedAt: z.string().datetime().nullable(),
});
export const proposalBlockChangeSchema = z.object({
    blockId: z.string(),
    title: z.string(),
    oldBody: z.string(),
    newBody: z.string(),
});
export const proposalQuestionPointSchema = z.object({
    id: z.string().uuid(),
    sourceUserMessageId: z.string().uuid(),
    answerMessageId: z.string().uuid(),
    question: z.string(),
    answer: z.string(),
    targetBlockId: z.string(),
});
export const proposalSchema = z.object({
    id: z.string().uuid(),
    conversationId: z.string().uuid(),
    termId: z.string().uuid(),
    baselineRevisionId: z.string().uuid(),
    status: z.enum(["ready", "applied", "rejected", "superseded"]),
    summary: z.string(),
    blockChanges: z.array(proposalBlockChangeSchema).min(1),
    questionPoints: z.array(proposalQuestionPointSchema),
    appliedRevisionId: z.string().uuid().nullable(),
    createdAt: z.string().datetime(),
});
export const applyProposalRequestSchema = z.object({
    baselineRevisionId: z.string().uuid(),
});
export const applyProposalResponseSchema = z.object({
    revision: revisionSummarySchema,
    proposalId: z.string().uuid(),
});
